/**
 * The report, as data (T-504).
 *
 * The document a user hands to someone else. Whoever reads it gives it a couple
 * of minutes at most, so a chronological dump of every data point is strictly
 * worse than a considered summary — completeness is not the same as usefulness.
 *
 * Every number here comes from the same functions the screens use. That is the
 * whole point of assembling the report in core: the app and the printed page
 * cannot disagree, and the person holding both has no reason to wonder which
 * one is wrong. Only the drawing is platform work.
 *
 * Nothing in this file knows what is being tracked. A report of body
 * measurements and a report of rainfall are the same code.
 */

import type { Entry, Tag } from '../model/entry.js'
import type { Timestamp } from '../model/timestamp.js'
import { compareTimestamps } from '../model/timestamp.js'
import type { Track } from '../model/track.js'
import { CORE_VERSION } from '../version.js'
import { adherenceFor, type Adherence } from './adherence.js'
import { buildOverlay, type Overlay } from './overlay.js'
import type { ResolvedRange } from './range.js'
import {
  bucketSeries,
  buildSeries,
  defaultBucketSize,
  type Aggregate,
  type BucketedSeries,
  type BucketSize,
  type CategoryCount,
  type Series,
} from './series.js'
import {
  aggregateDecimals,
  describeChange,
  describeQuantity,
  statisticsFor,
  trendOf,
  type Statistics,
  type Trend,
} from './statistics.js'
import { timeOfDayDistribution, type TimeOfDayDistribution } from './time-of-day.js'

/**
 * A trend as a sentence, built once and used everywhere.
 *
 * Here rather than in the component that first needed it, because the on-screen
 * panel and the printed page must not be able to word the same fact
 * differently — "rising" in the app beside "steady" in the document is worse
 * than either being absent.
 */
export function describeTrend(track: Track, trend: Trend): string {
  if (trend.kind === 'insufficientData') {
    // "Not enough yet" rather than an arrow. A regression through three
    // self-reported readings renders identically to one through three hundred,
    // and this document is one people make decisions from.
    return `Not enough yet (${String(trend.points)} of ${String(trend.required)})`
  }
  return trend.direction === 'steady'
    ? 'steady'
    : `${trend.direction} by ${describeChange(track, trend.change)}`
}

/**
 * One track's line in the summary table.
 *
 * A discriminated union, so the categorical branch has no `mean` to read. An
 * unordered enum cannot produce an average, and a meaningless number that
 * reaches a printed document is worse than a missing one — this makes it
 * unrepresentable rather than merely omitted from the layout.
 */
export type ReportRow =
  | {
      readonly kind: 'quantitative'
      readonly track: Track
      readonly count: number
      readonly statistics: Statistics
      readonly trend: Trend
      readonly meanLabel: string
      readonly rangeLabel: string
      readonly spreadLabel: string
      readonly trendLabel: string
    }
  | {
      readonly kind: 'categorical'
      readonly track: Track
      readonly count: number
      readonly counts: readonly CategoryCount[]
    }
  /** Nothing recorded for this track in this range. Said, not drawn empty. */
  | { readonly kind: 'noData'; readonly track: Track }

export function summariseTrack(track: Track, series: Series): ReportRow {
  if (series.kind === 'categorical') {
    if (series.points.length === 0) {
      return { kind: 'noData', track }
    }
    const counts = new Map<string, CategoryCount>()
    for (const point of series.points) {
      const seen = counts.get(point.label)
      counts.set(point.label, {
        optionId: typeof point.value === 'string' ? point.value : point.label,
        label: point.label,
        count: (seen?.count ?? 0) + 1,
      })
    }
    return {
      kind: 'categorical',
      track,
      count: series.points.length,
      counts: [...counts.values()].sort((a, b) => b.count - a.count),
    }
  }

  const statistics = statisticsFor(series)
  if (statistics === null) {
    return { kind: 'noData', track }
  }

  const trend = trendOf(series)
  const decimals = aggregateDecimals(track)

  return {
    kind: 'quantitative',
    track,
    count: statistics.count,
    statistics,
    trend,
    meanLabel: describeQuantity(track, Number(statistics.mean.toFixed(decimals))),
    rangeLabel: `${describeQuantity(track, statistics.min)} – ${describeQuantity(track, statistics.max)}`,
    spreadLabel: `±${statistics.standardDeviation.toFixed(Math.max(decimals, 1))}`,
    trendLabel: describeTrend(track, trend),
  }
}

// ─── Sparsity ─────────────────────────────────────────────────────────────────

/**
 * Below this share of days logged, the report says so rather than implying the
 * charts are a picture of the window they are drawn over.
 *
 * A chart of four days out of ninety looks exactly like a chart of ninety.
 * Whoever reads this has two minutes and no way to tell the difference.
 */
const SPARSE_COVERAGE = 0.25

/** Fewer logged days than this is sparse regardless of the window's length. */
const SPARSE_DAYS = 5

export type Sparsity =
  | { readonly kind: 'ok' }
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'sparse'
      readonly loggedDays: number
      readonly totalDays: number
    }

function sparsityOf(entryCount: number, adherence: Adherence): Sparsity {
  if (entryCount === 0) {
    return { kind: 'empty' }
  }
  if (adherence.loggedDays < SPARSE_DAYS || adherence.coverage < SPARSE_COVERAGE) {
    return {
      kind: 'sparse',
      loggedDays: adherence.loggedDays,
      totalDays: adherence.totalDays,
    }
  }
  return { kind: 'ok' }
}

// ─── The report ───────────────────────────────────────────────────────────────

export interface ReportSections {
  /** The overlay chart of every comparable track on one axis. */
  readonly overlay: boolean
  /** One page per track: its series and its time-of-day distribution. */
  readonly perTrack: boolean
  /** Tag frequency and notes in date order. */
  readonly appendix: boolean
}

export const ALL_REPORT_SECTIONS: ReportSections = {
  overlay: true,
  perTrack: true,
  appendix: true,
}

export interface ReportRequest {
  readonly range: ResolvedRange
  /** Chosen tracks, in the order they should appear. Archived ones welcome. */
  readonly tracks: readonly Track[]
  /** Entries already narrowed to the range. */
  readonly entries: readonly Entry[]
  readonly tags: readonly Tag[]
  readonly sections: ReportSections
  readonly generatedAt: Timestamp
  readonly title?: string
  readonly appVersion?: string
  readonly bucketSize?: BucketSize
  readonly aggregate?: Aggregate
}

export interface TagFrequency {
  readonly tag: Tag
  readonly count: number
}

export interface ReportNote {
  readonly entryId: string
  readonly at: Timestamp
  readonly note: string
  /** Present when the note was attached to one value rather than the moment. */
  readonly trackName?: string
}

export interface ReportAppendix {
  readonly tags: readonly TagFrequency[]
  readonly notes: readonly ReportNote[]
}

export interface TrackReport {
  readonly track: Track
  readonly series: Series
  readonly bucketed: BucketedSeries
  readonly timeOfDay: TimeOfDayDistribution
  readonly row: ReportRow
}

export interface ReportSummary {
  readonly entryCount: number
  readonly adherence: Adherence
  readonly rows: readonly ReportRow[]
  readonly sparsity: Sparsity
}

export interface ReportModel {
  readonly title: string
  readonly range: ResolvedRange
  readonly generatedAt: Timestamp
  readonly appVersion: string
  readonly bucketSize: BucketSize
  readonly summary: ReportSummary
  readonly tracks: readonly TrackReport[]
  readonly overlay: Overlay | null
  readonly appendix: ReportAppendix | null
}

/**
 * A neutral default.
 *
 * The report is generic across whatever tracks exist, and a default title that
 * described any particular subject matter would be a use case leaking into the
 * product.
 */
const DEFAULT_TITLE = 'anytracker report'

export function buildReport(request: ReportRequest): ReportModel {
  const { range, entries, tracks } = request
  const bucketSize = request.bucketSize ?? defaultBucketSize(range)
  const aggregate = request.aggregate ?? 'mean'
  const adherence = adherenceFor(entries, range)

  const trackReports: TrackReport[] = tracks.map((track) => {
    const series = buildSeries(track, entries, range)
    return {
      track,
      series,
      bucketed: bucketSeries(series, { size: bucketSize, aggregate }),
      timeOfDay: timeOfDayDistribution(series),
      row: summariseTrack(track, series),
    }
  })

  return {
    title:
      request.title?.trim() === '' ? DEFAULT_TITLE : (request.title ?? DEFAULT_TITLE),
    range,
    generatedAt: request.generatedAt,
    appVersion: request.appVersion ?? CORE_VERSION,
    bucketSize,
    summary: {
      entryCount: entries.length,
      adherence,
      rows: trackReports.map((report) => report.row),
      sparsity: sparsityOf(entries.length, adherence),
    },
    tracks: request.sections.perTrack ? trackReports : [],
    overlay: request.sections.overlay
      ? buildOverlay(
          tracks.map((track) => ({ track, entries })),
          { size: bucketSize, aggregate, range },
        )
      : null,
    appendix: request.sections.appendix
      ? appendixOf(entries, request.tags, tracks)
      : null,
  }
}

function appendixOf(
  entries: readonly Entry[],
  tags: readonly Tag[],
  tracks: readonly Track[],
): ReportAppendix {
  const counts = new Map<string, number>()
  for (const entry of entries) {
    for (const tagId of entry.tagIds) {
      counts.set(tagId, (counts.get(tagId) ?? 0) + 1)
    }
  }

  const notes: ReportNote[] = []
  const trackNames = new Map(tracks.map((track) => [track.id, track.name]))
  for (const entry of entries) {
    if (entry.note !== undefined && entry.note.trim() !== '') {
      notes.push({ entryId: entry.id, at: entry.at, note: entry.note })
    }
    for (const measurement of entry.measurements) {
      // Only for tracks this report covers. A report on two tracks out of ten
      // that carried notes about the other eight would be reporting on data it
      // does not otherwise show.
      if (!trackNames.has(measurement.trackId)) {
        continue
      }
      if (measurement.note !== undefined && measurement.note.trim() !== '') {
        notes.push({
          entryId: entry.id,
          at: entry.at,
          note: measurement.note,
          // Named, because a note about one value out of six is a different
          // statement from a note about the moment — and an appendix that
          // flattens the two is one nobody can act on.
          trackName: trackNames.get(measurement.trackId) ?? '',
        })
      }
    }
  }

  return {
    tags: tags
      .map((tag) => ({ tag, count: counts.get(tag.id) ?? 0 }))
      // Unused tags are dropped rather than listed at zero: a list of labels
      // nobody applied says nothing about the period being reported on.
      .filter((frequency) => frequency.count > 0)
      .sort((a, b) => b.count - a.count || a.tag.name.localeCompare(b.tag.name)),
    notes: notes.sort((a, b) => compareTimestamps(a.at, b.at)),
  }
}
